import { authClient } from "@superadmin/utils/auth.client";
import { BrandLogo } from "@voicely/ui-mui/components/BrandLogo";
import { EmailPasswordForm } from "@voicely/ui-mui/components/EmailPasswordForm";
import { GoogleIcon } from "@voicely/ui-mui/components/GoogleIcon";
import { Typography } from "@voicely/ui-mui/data-display/Typography";
import { Alert } from "@voicely/ui-mui/feedback/Alert";
import { Button } from "@voicely/ui-mui/form/Button";
import { Box } from "@voicely/ui-mui/layout/Box";
import { Container } from "@voicely/ui-mui/layout/Container";
import { Divider } from "@voicely/ui-mui/layout/Divider";
import { Paper } from "@voicely/ui-mui/layout/Paper";
import { Stack } from "@voicely/ui-mui/layout/Stack";
import { useState } from "react";
import { useSearchParams } from "react-router";

/**
 * Google, or email and password for an operator account provisioned by
 * another operator — the allowlist gate lives on the
 * server, so anybody can sign in here and only operators get past
 * `adminLoader`. The OAuth redirect URI stays on the main app origin
 * (better-auth's `baseURL` pins it); only the post-login `callbackURL` comes
 * back to this hostname, which is why this origin must be in the backend's
 * ALLOWED_ORIGINS.
 */
export const LoginPage = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [searchParams] = useSearchParams();
	const error = searchParams.get("error");

	const handleGoogleLogin = async () => {
		setIsLoading(true);

		const origin = window.location.origin;

		try {
			const data = await authClient.signIn.social(
				{
					provider: "google",
					callbackURL: `${origin}/dashboard`,
					errorCallbackURL: `${origin}/auth/login`,
					disableRedirect: true,
				},
				{ throw: true },
			);

			if (data?.url) window.location.href = data.url;
		} catch (err) {
			console.error("OAuth initiation failed:", err);
			setIsLoading(false);
		}
	};

	const handleEmailLogin = async ({ email, password }: { email: string; password: string }) => {
		await authClient.signIn.email({ email, password }, { throw: true });
		// No redirect URL comes back from email sign-in, only the session cookie.
		window.location.href = `${window.location.origin}/dashboard`;
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				px: 2,
				bgcolor: "surface.canvas",
			}}
		>
			<Container maxWidth="xs">
				<Paper variant="outlined" sx={{ p: 4, borderRadius: 4 }}>
					<Stack spacing={3} alignItems="center">
						<Box sx={{ textAlign: "center" }}>
							<BrandLogo variant="icon" height={40} sx={{ mx: "auto", mb: 1.5 }} />
							<Typography variant="h4">Voicely Superadmin</Typography>
							<Typography variant="body2" color="text.secondary">
								Operators only. Sign in to continue.
							</Typography>
						</Box>

						{error && (
							<Alert severity="error" sx={{ width: "100%" }}>
								{decodeURIComponent(error)}
							</Alert>
						)}

						<Button
							variant="contained"
							fullWidth
							size="large"
							onClick={handleGoogleLogin}
							disabled={isLoading}
							startIcon={<GoogleIcon width={18} height={18} />}
							sx={{ minHeight: 48 }}
						>
							{isLoading ? "Connecting..." : "Continue with Google"}
						</Button>

						<Divider sx={{ width: "100%", color: "text.secondary", typography: "caption" }}>
							or
						</Divider>

						<EmailPasswordForm onSubmit={handleEmailLogin} disabled={isLoading} />
					</Stack>
				</Paper>
			</Container>
		</Box>
	);
};
